import { AlertCircle, CheckCircle, Clock, RefreshCw, Wifi, WifiOff, X } from 'lucide-react'
import React, { useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import SimpleNotificationPrompt from '@/features/comunicacao/components/SimpleNotificationPrompt'
import { useBrowserMentionIndicator } from '@/features/comunicacao/hooks/useBrowserMentionIndicator'
import { useNotificationCount } from '@/features/comunicacao/hooks/useNotificationCount'
import AssistenteBotao from '@/features/ia/components/AssistenteBotao'
import AssistenteChat from '@/features/ia/components/AssistenteChat'
import InstallPWA from '@/features/sistema/components/InstallPWA'
import { OfflineStatusManager } from '@/features/sistema/components/OfflineIndicator'
import PWAUpdatePrompt from '@/features/sistema/components/PWAUpdatePrompt'

import { useAuth } from '../hooks/useAuth'
import { useDarkMode } from '../hooks/useDarkMode'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import Sidebar from '../pages/Sidebar'
import { useSyncStatus } from '../services/offilineSyncService'
import { supabase } from '../services/supabaseClient'
import { PageLoading } from './Loading'
import MobileTabBar from './MobileTabBar'

interface LayoutProps {
  children: React.ReactNode
}

// 🔒 Garante que cliente navegue apenas pelo portal do cliente
function ClientePortalGate() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // ✅ NÃO redireciona se não há usuário ou se está carregando
    if (!user || loading) return;
    
    // ✅ NÃO redireciona se já está na página de login
    if (location.pathname.includes('/login')) return;
    
    (async () => {
      try {
        const { data: u } = await supabase
          .from('usuarios')
          .select('role')
          .eq('id', user.id)
          .single();

        if (u?.role === 'cliente' && !location.pathname.startsWith('/cliente')) {
          navigate('/cliente', { replace: true });
        }
      } catch (error) {
        console.warn('Erro ao verificar role do usuário:', error);
      }
    })();
  }, [user, loading, location.pathname, navigate]);

  return null;
}


// ✅ COMPONENTE: Toast de conexão restaurada
function OnlineToast() {
  const isOnline = useOnlineStatus();
  const { pending } = useSyncStatus();
  const [showToast, setShowToast] = React.useState(false);
  const [wasOffline, setWasOffline] = React.useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline && isOnline) {
      setShowToast(true);
      setWasOffline(false);
      
      const timer = setTimeout(() => setShowToast(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  if (!showToast || !isOnline) return null;

  return (
    <div className="fixed top-20 right-4 bg-green-600 dark:bg-green-700 text-white px-4 py-3 rounded-lg shadow-lg z-50 font-ageo-regular text-sm max-w-sm border border-green-500">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wifi className="w-4 h-4" />
          <div>
            <div className="font-ageo-bold">Conexão restaurada!</div>
            {pending > 0 && (
              <div className="text-xs text-white/90">
                Sincronizando {pending} item{pending !== 1 ? 's' : ''}...
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowToast(false)}
          className="text-white/80 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ✅ COMPONENTE: Status de sincronização
function SyncStatusToast() {
  const { pending, failed, isSyncing } = useSyncStatus();
  const [showSync, setShowSync] = React.useState(false);
  const [lastMessage, setLastMessage] = React.useState('');

  useEffect(() => {
    if (pending > 0 || failed > 0 || isSyncing) {
      setShowSync(true);
      
      if (isSyncing) {
        setLastMessage(`Sincronizando ${pending} item${pending !== 1 ? 's' : ''}...`);
      } else if (failed > 0) {
        setLastMessage(`${failed} erro${failed !== 1 ? 's' : ''} de sincronização`);
      } else if (pending > 0) {
        setLastMessage(`${pending} item${pending !== 1 ? 's' : ''} pendente${pending !== 1 ? 's' : ''}`);
      }
    } else if (showSync && pending === 0 && failed === 0) {
      setLastMessage('Sincronização concluída!');
      const timer = setTimeout(() => {
        setShowSync(false);
        setLastMessage('');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [pending, failed, isSyncing, showSync]);

  if (!showSync) return null;

  return (
    <div className={`fixed bottom-20 left-4 text-white px-3 py-2 rounded-lg shadow-lg z-40 font-ageo-regular text-sm border max-w-xs ${
      failed > 0 
        ? 'bg-red-600 dark:bg-red-700 border-red-500'
        : isSyncing
        ? 'bg-blue-600 dark:bg-blue-700 border-blue-500'
        : lastMessage.includes('concluída')
        ? 'bg-green-600 dark:bg-green-700 border-green-500'
        : 'bg-yellow-600 dark:bg-yellow-700 border-yellow-500'
    }`}>
      <div className="flex items-center gap-2">
        {isSyncing ? (
          <RefreshCw className="w-3 h-3 animate-spin" />
        ) : failed > 0 ? (
          <AlertCircle className="w-3 h-3" />
        ) : lastMessage.includes('concluída') ? (
          <CheckCircle className="w-3 h-3" />
        ) : (
          <Clock className="w-3 h-3" />
        )}
        <span className="truncate">{lastMessage}</span>
      </div>
    </div>
  );
}

// ✅ COMPONENTE: Barra offline superior
function OfflineTopBar() {
  const isOnline = useOnlineStatus();
  const { pending, failed } = useSyncStatus();

  if (isOnline) return null;

  return (
    <div className="fixed top-16 left-1/2 transform -translate-x-1/2 bg-red-600 dark:bg-red-700 text-white px-4 py-2 rounded-lg text-xs font-ageo-regular z-50 shadow-lg border border-red-700 dark:border-red-800 max-w-sm">
      <div className="flex items-center justify-center gap-2">
        <WifiOff className="w-3 h-3" />
        <span>
          Offline
          {pending > 0 && ` • ${pending} pendente${pending !== 1 ? 's' : ''}`}
          {failed > 0 && ` • ${failed} erro${failed !== 1 ? 's' : ''}`}
        </span>
      </div>
    </div>
  );
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, loading } = useAuth()
  const location = useLocation();
  const isCronograma = /\/obras\/[^/]+\/cronograma$/.test(location.pathname);
  
  // 🔐 Se o Supabase sinalizar SIGNED_OUT, levar para o login
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        window.location.assign('/ERP-Acor/login'); // mantém basename
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  
  // ✅ Unifica com o hook global (usa 'acor-modo-escuro' + aplica html.dark)
  const [modoEscuro, toggleModoEscuro] = useDarkMode();
  // 🔔 Menções -> efeitos no navegador (título, favicon, App Badge)
  const { mentions } = useNotificationCount();
  useBrowserMentionIndicator(mentions, { titleBase: 'ACOR ERP' });

  // ✅ Detectar mobile de forma segura
  const [isMobile, setIsMobile] = React.useState(() => {
    try {
      return window.innerWidth < 768;
    } catch (error) {
      console.warn('Erro ao detectar mobile, usando fallback desktop');
      return false;
    }
  });

  // ✅ Listener para resize
  React.useEffect(() => {
    try {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    } catch (error) {
      console.warn('Erro ao configurar resize listener:', error);
    }
  }, []);

  // ❌ Removido: aplicar classe e salvar por conta própria (o hook já faz isso)

  // ✅ Função para clonar children de forma segura
  const renderChildrenWithProps = (children: React.ReactNode): React.ReactNode => {
    try {
      if (React.isValidElement(children)) {
        // Verificar se é um componente React (função ou classe)
        const isReactComponent =
          typeof children.type === 'function' ||
          (typeof children.type === 'object' && children.type !== null);
        if (isReactComponent) {
          // Se precisar repassar, mantém compatibilidade e corrige spread
          return React.cloneElement(children as any, {
            ...(children as any).props,
            modoEscuro,
            toggleModoEscuro,
            isMobile
          });
        }
      }
      // Se não for componente React, retorna como está
      return children;
    } catch (error) {
      console.error('Erro ao renderizar children com props:', error);
      return children;
    }
  }

  if (loading) {
    return <PageLoading message="Verificando acesso..." />
  }

  // Se não está logado, redireciona para a página principal (login)
  if (!user) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="min-h-screen bg-background dark:bg-gray-900 transition-colors duration-200">
      {/* 🔒 Enforce portal do cliente (hooks ficam no filho) */}
      <ClientePortalGate />

      {/* ✅ NOVOS: Indicadores Offline */}
      <OfflineTopBar />
      <OnlineToast />
      <SyncStatusToast />
      
      {/* ✅ PWA: Indicadores de Status (mantido original) */}
      <OfflineStatusManager />
      <PWAUpdatePrompt />
      
      {/* ✅ Shell principal: Sidebar no desktop, Mobile Shell no mobile */}
      <div className="transition-all duration-200 bg-transparent">
        {isMobile ? (
          /* 📱 MOBILE SHELL — único scroll container */
          <div id="mobile-shell" className="md:hidden h-[100svh] min-h-0 overflow-auto flex flex-col">
            {/* Conteúdo que rola */}
            <main
              className="flex-1 pt-4"
              style={{
                // reserva espaço real p/ a TabBar sticky + safe-area
                paddingBottom: 'calc(var(--tabbar-bottom-offset, 76px) + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <div
                className={
                  isCronograma
                    ? "w-full bg-transparent"
                    : "w-full max-w-[1600px] 2xl:max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 bg-transparent"
                }
              >
                {renderChildrenWithProps(children)}
              </div>
            </main>
            {/* TabBar agora é filha do mesmo shell e gruda com sticky */}
            <MobileTabBar />
          </div>
        ) : (
          // 🖥️ Desktop: Sidebar fixa à esquerda, sem Navbar no topo
          <div className="flex">
            <Sidebar />
            <main className="flex-1 pt-4 pb-8">
              <div
                className={
                  isCronograma
                    ? "w-full bg-transparent" // ocupa só a área útil (respeita a sidebar)
                    : "w-full max-w-[1600px] 2xl:max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 bg-transparent"
                }
              >
                {renderChildrenWithProps(children)}
              </div>
            </main>
          </div>
        )}
      </div>
      
      {/* (a TabBar móvel já está dentro do Mobile Shell) */}
      
      {/* ✅ PWA: Banner de Instalação */}
      <InstallPWA />
      
      {/* ✅ PWA: Prompt de Atualização */}
      <PWAUpdatePrompt />
      
      {/* ✅ NOVO: Prompt Simples de Notificações */}
      <SimpleNotificationPrompt />
      
      {/* ✅ Assistente Virtual */}
      <AssistenteBotao />
      
      {/* ✅ Chat do Assistente */}
      <AssistenteChat />
    </div>
  )
}

export default Layout
